import React, { memo } from "react";
import {
  IconArrowLeft,
  IconDotsVertical,
  IconPhone,
  IconRobot,
  IconUserPlus,
  IconCheck,
  IconBrandWhatsapp,
} from "@tabler/icons-react";
import { Dropdown } from "react-bootstrap";
import { ServiceStatusBadge } from "../common";
import ChannelBadge from "../common/ChannelBadge";
import { formatPhone } from "../../utils/formatters";
import "./ChatHeader.css";

/**
 * Header do chat com informações do contato e ações
 */
const ChatHeader = memo(({
  conversation,
  contact,
  botStatus, // 1 = ativo, 0 = desativado
  onBack,
  onToggleBot,
  onStartService,
  onTransfer,
  onFinish,
  onViewInfo,
  onResetMemory,
}) => {
  // Verifica se a IA está ativa (1 = ativo, 0 = desativado)
  const isAIActive = botStatus === 1;
  if (!conversation) {
    return (
      <div className="sc-chat-header sc-chat-header--empty">
        <IconBrandWhatsapp size={24} className="sc-chat-header__empty-icon" />
        <span>Selecione uma conversa</span>
      </div>
    );
  }

  const channel = conversation.channel || "whatsapp";
  const contactName = contact?.name || conversation.name || (
    channel === "instagram" ? conversation.channel_user_id : formatPhone(conversation.phone)
  );
  const contactPhone = conversation.phone;
  const serviceStatus = conversation.service_status;
  const avatarUrl = conversation.avatar || contact?.profile_picture ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&background=random`;

  return (
    <div className="sc-chat-header">
      {/* Botão voltar (mobile) */}
      <button
        type="button"
        className="sc-chat-header__back"
        onClick={onBack}
        aria-label="Voltar para conversas"
      >
        <IconArrowLeft size={20} />
      </button>

      {/* Avatar e info do contato */}
      <div className="sc-chat-header__contact" onClick={onViewInfo}>
        <div style={{ position: "relative", display: "inline-block" }}>
          <img
            src={avatarUrl}
            alt={contactName}
            className="sc-chat-header__avatar"
          />
          <ChannelBadge channel={channel} variant="avatar" />
        </div>
        <div className="sc-chat-header__info">
          <span className="sc-chat-header__name">{contactName}</span>
          <div className="sc-chat-header__meta">
            <span className="sc-chat-header__phone">
              {channel === "instagram"
                ? (conversation.channel_user_id || contactPhone)
                : formatPhone(contactPhone)}
            </span>
            <ChannelBadge
              channel={channel}
              variant="inline"
              identity={conversation.provider_identity}
            />
            {serviceStatus && (
              <ServiceStatusBadge status={serviceStatus} size="small" />
            )}
          </div>
        </div>
      </div>

      {/* Ações */}
      <div className="sc-chat-header__actions">
        {/* Toggle Bot com indicador de status - escondido no mobile */}
        <button
          className={`sc-chat-header__action-btn sc-chat-header__bot-btn sc-chat-header__action-btn--desktop ${isAIActive ? "sc-chat-header__bot-btn--active" : "sc-chat-header__bot-btn--inactive"}`}
          onClick={onToggleBot}
          title={isAIActive ? "Desativar IA" : "Ativar IA"}
        >
          <IconRobot size={20} />
          <span className={`sc-chat-header__bot-indicator ${isAIActive ? "sc-chat-header__bot-indicator--active" : "sc-chat-header__bot-indicator--inactive"}`} />
        </button>

        {/* Ações de atendimento baseadas no status - escondidas no mobile */}
        {(serviceStatus === "new" || serviceStatus === "finished") && (
          <button
            className="sc-chat-header__action-btn sc-chat-header__action-btn--primary sc-chat-header__action-btn--desktop"
            onClick={onStartService}
            title={serviceStatus === "finished" ? "Reativar Atendimento" : "Iniciar Atendimento"}
          >
            <IconUserPlus size={20} />
          </button>
        )}

        {(serviceStatus === "in_service" || serviceStatus === "waiting") && (
          <button
            className="sc-chat-header__action-btn sc-chat-header__action-btn--success sc-chat-header__action-btn--desktop"
            onClick={onFinish}
            title="Finalizar Atendimento"
          >
            <IconCheck size={20} />
          </button>
        )}

        {/* Menu dropdown */}
        <Dropdown align="end">
          <Dropdown.Toggle
            as="button"
            className="sc-chat-header__action-btn sc-chat-header__menu-toggle"
          >
            <IconDotsVertical size={20} />
          </Dropdown.Toggle>

          <Dropdown.Menu className="sc-chat-header__menu">
            {/* Toggle IA - apenas no mobile */}
            <Dropdown.Item
              onClick={onToggleBot}
              className={`sc-chat-header__menu-item--mobile ${isAIActive ? "text-success" : "text-danger"}`}
            >
              <IconRobot size={16} />
              {isAIActive ? "Desativar IA" : "Ativar IA"}
              <span className={`sc-chat-header__menu-indicator ${isAIActive ? "sc-chat-header__menu-indicator--active" : "sc-chat-header__menu-indicator--inactive"}`} />
            </Dropdown.Item>

            <Dropdown.Divider className="sc-chat-header__menu-divider--mobile" />

            {/* Iniciar atendimento - apenas no mobile */}
            {(serviceStatus === "new" || serviceStatus === "finished") && (
              <>
                <Dropdown.Item
                  onClick={onStartService}
                  className="sc-chat-header__menu-item--mobile text-primary"
                >
                  <IconUserPlus size={16} />
                  {serviceStatus === "finished" ? "Reativar Atendimento" : "Iniciar Atendimento"}
                </Dropdown.Item>
                <Dropdown.Divider className="sc-chat-header__menu-divider--mobile" />
              </>
            )}

            {/* Finalizar atendimento - apenas no mobile */}
            {(serviceStatus === "in_service" || serviceStatus === "waiting") && (
              <>
                <Dropdown.Item
                  onClick={onFinish}
                  className="sc-chat-header__menu-item--mobile text-success"
                >
                  <IconCheck size={16} />
                  Finalizar Atendimento
                </Dropdown.Item>
                <Dropdown.Divider className="sc-chat-header__menu-divider--mobile" />
              </>
            )}

            <Dropdown.Item onClick={onViewInfo}>
              <IconPhone size={16} />
              Ver dados do contato
            </Dropdown.Item>

            {serviceStatus === "in_service" && (
              <>
                <Dropdown.Item onClick={onTransfer}>
                  <IconUserPlus size={16} />
                  Transferir atendimento
                </Dropdown.Item>
              </>
            )}

            <Dropdown.Divider />

            <Dropdown.Item onClick={onResetMemory}>
              <IconRobot size={16} />
              Limpar memória do agente
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
});

ChatHeader.displayName = "ChatHeader";

export default ChatHeader;
